import React, { useState, useEffect } from 'react';
import { 
  Facebook, 
  Twitter, 
  Linkedin, 
  Instagram, 
  Mail, 
  Phone, 
  MapPin,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import httpClient from '../../Api/axios';
import { useTranslation } from 'react-i18next';

function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [contactInfo, setContactInfo] = useState({
    phone: '+1 (555) 123-4567',
    email: 'info@kandx.com',
    address: '123 Tech Street, Suite 100, Toronto, ON M5H 2N2',
    working_hours: 'Mon-Fri: 9AM-6PM EST'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const socialIcons = [
    { icon: Twitter, label: t('footer.social.twitter'), url: 'https://x.com/KanadxSolutions' },
    { icon: Linkedin, label: t('footer.social.linkedin'), url: 'https://www.linkedin.com/company/kanadxsolutions/' },
    { icon: Facebook, label: t('footer.social.facebook'), url: 'https://www.facebook.com/KanadxSolutions' },
    { icon: Instagram, label: t('footer.social.instagram'), url: 'https://www.instagram.com/KanadxSolutions/' },
  ];

  const services = [
    { label: t('footer.services.generativeAI'), path: '/generative-ai' },
    { label: t('footer.services.webDev'), path: '/web-development' },
    { label: t('footer.services.cloud'), path: '/cloud-solutions' },
    { label: t('footer.services.mobile'), path: '/mobile-development' },
    { label: t('footer.services.database'), path: '/database-management' },
    { label: t('footer.services.digitalMarketing'), path: '/digital-marketing' },
    { label: t('footer.services.cybersecurity'), path: '/cybersecurity' },
    { label: t('footer.services.devops'), path: '/devops-services' },
    { label: t('footer.services.consulting'), path: '/technology-consulting' },
    { label: t('footer.services.remoteWorkplace'), path: '/remote-workplace' },
    { label: t('footer.services.disasterRecovery'), path: '/backup-disaster-recovery' },
    { label: t('footer.services.helpDesk'), path: '/help-desc' },
  ];

  const quickLinks = [
    { label: t('footer.quickLinks.home'), path: '/' },
    { label: t('footer.quickLinks.about'), path: '/about' },
    { label: t('footer.quickLinks.partners'), path: '/partners' },
    { label: t('footer.quickLinks.portfolio'), path: '/portfolio' },
    { label: t('footer.quickLinks.team'), path: '/team' },
    { label: t('footer.quickLinks.contact'), path: '/contact' },
    { label: t('footer.quickLinks.itSolutions'), path: '/IT-Solutions' },
  ];

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await httpClient.get('/getall__footer');
        
        let footerData = response.data;
        
        if (Array.isArray(footerData) && footerData.length > 0) {
          footerData = footerData[0];
        }
        
        if (footerData && footerData.data) {
          footerData = footerData.data;
        }
        
        if (Array.isArray(footerData) && footerData.length > 0) {
          footerData = footerData[0];
        }
        
        if (footerData) {
          setContactInfo({
            phone: footerData.phone || contactInfo.phone,
            email: footerData.email || contactInfo.email,
            address: footerData.address || contactInfo.address,
            working_hours: footerData.working_hours || contactInfo.working_hours
          });
        } else {
          setError(t('footer.errors.fetchError'));
        }
      } catch (err) {
        console.error('Error fetching contact info:', err);
        setError(t('footer.errors.fetchError'));
      } finally {
        setLoading(false);
      }
    };
    fetchContactInfo();
  }, [t]);

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="container mx-auto px-6 sm:px-8 lg:px-16 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-y-8 gap-x-6 lg:gap-x-8">
          {/* Company Logo - 4 columns */}
          <div className="lg:col-span-4 space-y-4">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="/logooo.jpeg"
                alt="KANADX Logo"
                className="h-16 w-16 w-auto object-contain"
              />
              <div>
                <h3 className="text-xl font-bold text-white">KANADX</h3>
                <p className="text-sm text-gray-300">
                  {t('footer.company.tagline')}
                  <br />
                  {t('footer.company.tagline2', { defaultValue: 'Real Accountability.' })}
                </p>
              </div>
            </Link>
            <p className="text-gray-300 text-sm leading-relaxed">
              {t('footer.company.description')}
            </p>
            
            {/* Company Information Section */}
            <div className="pt-2 border-t border-gray-800 mt-4 space-y-3">
              {/* Division Information */}
              <div>
                <p className="text-gray-400 text-xs font-medium mb-1">
                  {t('footer.company.division')}
                </p>
                <p className="text-white font-semibold text-sm">
                  {t('footer.company.parentCompany', { defaultValue: 'SIXXER MIGRATE INC' })}
                </p>
              </div>
            </div>
            
            {/* Social Media Icons */}
            <div className="pt-4">
              <h4 className="text-gray-300 text-sm font-medium mb-3">
                {t('footer.social.connect')}
              </h4>
              <div className="flex space-x-3">
                {socialIcons.map((social) => (
                  <a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-gray-800 hover:bg-red-900/30 flex items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-red-900/20"
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5 text-gray-300 hover:text-white transition-colors" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links - 2 columns */}
          <div className="lg:col-span-2">
            <h3 className="text-base font-bold text-white mb-4 pb-2 border-b border-gray-800">
              {t('footer.quickLinks.title')}
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="flex items-center text-gray-300 hover:text-red-400 transition-colors group text-sm"
                  >
                    <ChevronRight className="w-4 h-4 mr-2 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services - 3 columns */}
          <div className="lg:col-span-3">
            <h3 className="text-base font-bold text-white mb-4 pb-2 border-b border-gray-800">
              {t('footer.services.title')}
            </h3>
            <ul className="space-y-2">
              {services.slice(0, 12).map((service) => (
                <li key={service.label}>
                  <Link
                    to={service.path}
                    className="flex items-center text-gray-300 hover:text-red-400 transition-colors group text-sm"
                  >
                    <ChevronRight className="w-4 h-4 mr-2 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - 3 columns */}
          <div className="lg:col-span-3 space-y-4">
            <div>
              <h3 className="text-base font-bold text-white mb-4 pb-2 border-b border-gray-800">
                {t('footer.contact.title')}
              </h3>
              
              <div className="space-y-4">
                {/* Canada Phone - Primary */}
                <div className="flex items-start space-x-2">
                  <div className="w-8 h-8 rounded-lg bg-red-900/30 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-xs">
                      {t('footer.contact.canadaPhonePrimary')}
                    </p>
                    {loading ? (
                      <div className="h-4 w-24 bg-gray-800 animate-pulse rounded mt-1"></div>
                    ) : error ? (
                      <p className="text-red-400 text-xs mt-1">{error}</p>
                    ) : (
                      <p className="text-white font-medium text-sm">{contactInfo.phone}</p>
                    )}
                  </div>
                </div>
                
                {/* Canada Phone - Secondary (Static) */}
                <div className="flex items-start space-x-2">
                  <div className="w-8 h-8 rounded-lg bg-red-900/30 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-xs">
                      {t('footer.contact.canadaPhoneSecondary')}
                    </p>
                    <p className="text-white font-medium text-sm">
                      {t('footer.contact.canadaPhoneSecondaryNumber', { defaultValue: '+1 (416) 700-7091' })}
                    </p>
                  </div>
                </div>
                
                {/* Email */}
                <div className="flex items-start space-x-2">
                  <div className="w-8 h-8 rounded-lg bg-red-900/30 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-xs">
                      {t('footer.contact.email')}
                    </p>
                    {loading ? (
                      <div className="h-4 w-32 bg-gray-800 animate-pulse rounded mt-1"></div>
                    ) : error ? (
                      <p className="text-red-400 text-xs mt-1">{error}</p>
                    ) : (
                      <p className="text-white font-medium text-sm">{contactInfo.email}</p>
                    )}
                  </div>
                </div>
                
                {/* Address - Canada Office */}
                <div className="flex items-start space-x-2">
                  <div className="w-8 h-8 rounded-lg bg-red-900/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-xs">
                      {t('footer.contact.canadaOffice')}
                    </p>
                    {loading ? (
                      <div className="h-8 w-40 bg-gray-800 animate-pulse rounded mt-1"></div>
                    ) : error ? (
                      <p className="text-red-400 text-xs mt-1">{error}</p>
                    ) : (
                      <p className="text-white font-medium text-xs">{contactInfo.address}</p>
                    )}
                    
                    {/* Static India Office Address */}
                    <div className="mt-2 pt-2 border-t border-gray-800">
                      <p className="text-gray-300 text-xs mb-1">
                        {t('footer.contact.indiaOffice1')}
                      </p>
                      <p className="text-white font-medium text-xs">
                        {t('footer.contact.indiaAddress1', { 
                          defaultValue: 'Office no. 409, 4th Floor, Rajdhani Complex, Pune - Satara Rd, near Shankar Maharaj Math, KK Market, Balaji Nagar, Pune, Maharashtra 411043'
                        })}
                      </p>
                    </div>
                    {/* ==================== */}
                    <div className="mt-2 pt-2 border-t border-gray-800">
                      <p className="text-gray-300 text-xs mb-1">
                        {t('footer.contact.indiaOffice2')}
                      </p>
                      <p className="text-white font-medium text-xs">
                        {t('footer.contact.indiaAddress2', { 
                          defaultValue: 'Office no. 304, Navale ICON, Near Navale Bridge, Wadgaon Budruk, Pune, Maharashtra 411041'
                        })}
                      </p>
                    </div>
                    {/* ========================== */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="text-gray-300 text-sm text-center">
              © {currentYear} KANADX. {t('footer.bottom.rights')}
            </div>
            
            <div className="flex items-center justify-center">
              <Link 
                to="/privacy&policy" 
                className="text-gray-300 hover:text-red-400 transition-colors text-sm"
              >
                {t('footer.bottom.privacyPolicy', { defaultValue: 'Privacy Policy' })}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;